<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-breadcrumbs :items="breadcrumbs">
          <template v-slot:divider>
            <v-icon>mdi-forward</v-icon>
          </template>
        </v-breadcrumbs>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-book-education-outline</v-icon>
            Learner Syllabus Management
          </v-card-title>

          <v-card-text class="pa-0">
            <v-tabs
              v-model="activeTab"
              background-color="grey lighten-4"
              color="primary"
              show-arrows
            >
              <v-tab href="#tab-syllabuses">
                <v-icon left small>mdi-book-open-variant</v-icon>
                Syllabuses
              </v-tab>
              <v-tab href="#tab-skills">
                <v-icon left small>mdi-playlist-check</v-icon>
                Skills
              </v-tab>
              <v-tab href="#tab-steps">
                <v-icon left small>mdi-stairs</v-icon>
                Steps
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- ===== SYLLABUSES TAB ===== -->
              <v-tab-item value="tab-syllabuses">
                <v-container class="pa-4">
                  <v-row>
                    <v-col cols="12">
                      <h3 class="section-subtitle mb-4">Add New Syllabus</h3>
                    </v-col>
                  </v-row>

                  <v-form @submit.prevent="submitSyllabus">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.trim="syllabusForm.name"
                          label="Syllabus Name *"
                          outlined
                          dense
                          counter="150"
                          :error-messages="syllabusNameErrors"
                          @input="$v.syllabusForm.name.$touch()"
                          @blur="$v.syllabusForm.name.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.trim="syllabusForm.version"
                          label="Version *"
                          outlined
                          dense
                          counter="20"
                          hint="Letters, numbers, dots and hyphens (e.g. V1, V1.0, R3-a)"
                          persistent-hint
                          :error-messages="syllabusVersionErrors"
                          @input="$v.syllabusForm.version.$touch()"
                          @blur="$v.syllabusForm.version.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-menu
                          v-model="effectiveFromMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              :value="syllabusForm.effectiveFrom"
                              label="Effective From *"
                              readonly
                              outlined
                              dense
                              prepend-inner-icon="mdi-calendar"
                              v-on="on"
                              :error-messages="syllabusEffectiveFromErrors"
                              @blur="$v.syllabusForm.effectiveFrom.$touch()"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="syllabusForm.effectiveFrom"
                            locale="en-gb"
                            :min="tomorrowDate"
                            no-title
                            @input="
                              effectiveFromMenu = false;
                              $v.syllabusForm.effectiveFrom.$touch();
                            "
                          ></v-date-picker>
                        </v-menu>
                        <v-alert
                          type="info"
                          dense
                          text
                          class="mt-1 mb-0 caption"
                          style="font-size: 0.78rem !important"
                        >
                          Note: Setting this date will
                          <strong>not</strong> automatically activate the
                          syllabus. It is a reminder only.
                        </v-alert>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <v-btn
                          color="primary"
                          type="submit"
                          :loading="syllabusSubmitting"
                        >
                          <v-icon left>mdi-plus-circle-outline</v-icon>
                          Add Syllabus
                        </v-btn>
                        <v-btn
                          text
                          class="ml-2"
                          @click="resetSyllabusForm"
                          :disabled="syllabusSubmitting"
                        >
                          Clear
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-form>

                  <v-divider class="my-6"></v-divider>

                  <v-data-table
                    :headers="syllabusHeaders"
                    :items="getSyllabuses"
                    :loading="!isSyllabusListLoaded"
                    loading-text="Loading syllabuses..."
                    item-key="id"
                    class="elevation-1"
                    @click:row="onSyllabusRowClick"
                    style="cursor: pointer"
                  >
                    <template v-slot:top>
                      <v-toolbar flat>
                        <v-toolbar-title>Syllabuses on System</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-chip small color="primary" outlined class="mr-2">
                          Click a row to view its Skills
                        </v-chip>
                      </v-toolbar>
                    </template>

                    <template v-slot:[`item.status`]="{ item }">
                      <!-- Overlay intercepts click so the switch never visually toggles before confirmation -->
                      <div style="position: relative; display: inline-flex">
                        <v-switch
                          :input-value="item.status === 1"
                          :disabled="item.status === 1"
                          color="success"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                        ></v-switch>
                        <div
                          v-if="item.status !== 1"
                          style="
                            position: absolute;
                            inset: 0;
                            cursor: pointer;
                            z-index: 1;
                          "
                          @click.stop="onSyllabusToggle(item)"
                        ></div>
                      </div>
                    </template>

                    <template v-slot:[`item.effective_from`]="{ item }">
                      {{ item.effective_from || "—" }}
                    </template>

                    <template v-slot:[`item.created_on`]="{ item }">
                      {{ item.created_on | dateReOrderRMtime }}
                    </template>

                    <template v-slot:no-data>
                      <v-alert type="info" outlined class="ma-4">
                        No syllabuses found. Add one above.
                      </v-alert>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

              <!-- ===== SKILLS TAB ===== -->
              <v-tab-item value="tab-skills">
                <v-container class="pa-4">
                  <v-row>
                    <v-col cols="12" md="5">
                      <v-autocomplete
                        v-model="selectedSyllabusForSkills"
                        :items="getSyllabuses"
                        item-text="name"
                        item-value="id"
                        label="Select Syllabus *"
                        outlined
                        dense
                        clearable
                        prepend-inner-icon="mdi-book-open-variant"
                        @change="onSyllabusSelectedForSkills"
                      >
                        <template v-slot:item="{ item }">
                          <span
                            >{{ item.name }}
                            <v-chip
                              x-small
                              class="ml-1"
                              :color="item.status === 1 ? 'success' : 'grey'"
                              dark
                              >{{ item.version }}</v-chip
                            ></span
                          >
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <template v-if="selectedSyllabusForSkills">
                    <v-row>
                      <v-col cols="12">
                        <h3 class="section-subtitle mb-4">Add New Skill</h3>
                      </v-col>
                    </v-row>

                    <v-form @submit.prevent="submitSkill">
                      <v-row>
                        <v-col cols="12" md="2">
                          <v-text-field
                            v-model.number="skillForm.skillNumber"
                            label="Skill Number *"
                            outlined
                            dense
                            type="number"
                            :hint="`Next expected: ${nextSkillNumber}`"
                            persistent-hint
                            :error-messages="skillNumberErrors"
                            @input="$v.skillForm.skillNumber.$touch()"
                            @blur="$v.skillForm.skillNumber.$touch()"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model.trim="skillForm.skillName"
                            label="Skill Name *"
                            outlined
                            dense
                            counter="150"
                            :error-messages="skillNameErrors"
                            @input="$v.skillForm.skillName.$touch()"
                            @blur="$v.skillForm.skillName.$touch()"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-textarea
                            v-model.trim="skillForm.skillDescription"
                            label="Skill Description (optional)"
                            outlined
                            dense
                            auto-grow
                            rows="1"
                            counter="500"
                            :error-messages="skillDescriptionErrors"
                            @input="$v.skillForm.skillDescription.$touch()"
                            @blur="$v.skillForm.skillDescription.$touch()"
                          ></v-textarea>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <v-btn
                            color="primary"
                            type="submit"
                            :loading="skillSubmitting"
                          >
                            <v-icon left>mdi-plus-circle-outline</v-icon>
                            Add Skill
                          </v-btn>
                          <v-btn
                            text
                            class="ml-2"
                            @click="resetSkillForm"
                            :disabled="skillSubmitting"
                          >
                            Clear
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-form>

                    <v-divider class="my-6"></v-divider>

                    <v-data-table
                      :headers="skillHeaders"
                      :items="skillsForSelectedSyllabus"
                      :loading="!isSkillListLoaded"
                      loading-text="Loading skills..."
                      item-key="skill_id"
                      class="elevation-1"
                      @click:row="onSkillRowClick"
                      style="cursor: pointer"
                    >
                      <template v-slot:top>
                        <v-toolbar flat>
                          <v-toolbar-title
                            >Skills for Selected Syllabus</v-toolbar-title
                          >
                          <v-spacer></v-spacer>
                          <v-chip small color="primary" outlined class="mr-2">
                            Click a row to view its Steps
                          </v-chip>
                        </v-toolbar>
                      </template>

                      <template v-slot:[`item.status`]="{ item }">
                        <v-switch
                          :input-value="item.status === 1"
                          color="success"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                          @change="onSkillToggle(item, $event)"
                          @click.stop
                        ></v-switch>
                      </template>

                      <template v-slot:[`item.created_on`]="{ item }">
                        {{ item.created_on | dateReOrderRMtime }}
                      </template>

                      <template v-slot:no-data>
                        <v-alert type="info" outlined class="ma-4">
                          No skills found for this syllabus. Add one above.
                        </v-alert>
                      </template>
                    </v-data-table>
                  </template>

                  <v-alert v-else type="info" outlined class="ma-4">
                    Please select a syllabus above to manage its skills.
                  </v-alert>
                </v-container>
              </v-tab-item>

              <!-- ===== STEPS TAB ===== -->
              <v-tab-item value="tab-steps">
                <v-container class="pa-4">
                  <v-row>
                    <v-col cols="12" md="5">
                      <v-autocomplete
                        v-model="selectedSyllabusForSteps"
                        :items="getSyllabuses"
                        item-text="name"
                        item-value="id"
                        label="Select Syllabus *"
                        outlined
                        dense
                        clearable
                        prepend-inner-icon="mdi-book-open-variant"
                        @change="onSyllabusSelectedForSteps"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="5">
                      <v-autocomplete
                        v-model="selectedSkillForSteps"
                        :items="skillsForStepsDropdown"
                        item-text="skill_name"
                        item-value="skill_id"
                        label="Select Skill *"
                        outlined
                        dense
                        clearable
                        prepend-inner-icon="mdi-playlist-check"
                        :disabled="!selectedSyllabusForSteps"
                        @change="onSkillSelectedForSteps"
                      >
                        <template v-slot:item="{ item }">
                          <span
                            >#{{ item.skill_number }} —
                            {{ item.skill_name }}</span
                          >
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <template v-if="selectedSkillForSteps">
                    <v-row>
                      <v-col cols="12">
                        <h3 class="section-subtitle mb-4">Add New Step</h3>
                      </v-col>
                    </v-row>

                    <v-form @submit.prevent="submitStep">
                      <v-row>
                        <v-col cols="12" md="2">
                          <v-text-field
                            v-model.number="stepForm.stepNumber"
                            label="Step Number *"
                            outlined
                            dense
                            type="number"
                            :hint="`Next expected: ${nextStepNumber}`"
                            persistent-hint
                            :error-messages="stepNumberErrors"
                            @input="$v.stepForm.stepNumber.$touch()"
                            @blur="$v.stepForm.stepNumber.$touch()"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model.trim="stepForm.stepName"
                            label="Step Name *"
                            outlined
                            dense
                            counter="150"
                            :error-messages="stepNameErrors"
                            @input="$v.stepForm.stepName.$touch()"
                            @blur="$v.stepForm.stepName.$touch()"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-textarea
                            v-model.trim="stepForm.stepDescription"
                            label="Step Description (optional)"
                            outlined
                            dense
                            auto-grow
                            rows="1"
                            counter="500"
                            :error-messages="stepDescriptionErrors"
                            @input="$v.stepForm.stepDescription.$touch()"
                            @blur="$v.stepForm.stepDescription.$touch()"
                          ></v-textarea>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <v-btn
                            color="primary"
                            type="submit"
                            :loading="stepSubmitting"
                          >
                            <v-icon left>mdi-plus-circle-outline</v-icon>
                            Add Step
                          </v-btn>
                          <v-btn
                            text
                            class="ml-2"
                            @click="resetStepForm"
                            :disabled="stepSubmitting"
                          >
                            Clear
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-form>

                    <v-divider class="my-6"></v-divider>

                    <v-data-table
                      :headers="stepHeaders"
                      :items="stepsForSelectedSkill"
                      :loading="!isStepListLoaded"
                      loading-text="Loading steps..."
                      item-key="step_id"
                      class="elevation-1"
                    >
                      <template v-slot:top>
                        <v-toolbar flat>
                          <v-toolbar-title
                            >Steps for Selected Skill</v-toolbar-title
                          >
                        </v-toolbar>
                      </template>

                      <template v-slot:[`item.status`]="{ item }">
                        <v-switch
                          :input-value="item.status === 1"
                          color="success"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                          @change="onStepToggle(item, $event)"
                        ></v-switch>
                      </template>

                      <template v-slot:[`item.created_on`]="{ item }">
                        {{ item.created_on | dateReOrderRMtime }}
                      </template>

                      <template v-slot:no-data>
                        <v-alert type="info" outlined class="ma-4">
                          No steps found for this skill. Add one above.
                        </v-alert>
                      </template>
                    </v-data-table>
                  </template>

                  <v-alert
                    v-else-if="!selectedSyllabusForSteps"
                    type="info"
                    outlined
                    class="ma-4"
                  >
                    Please select a syllabus and skill above to manage steps.
                  </v-alert>
                  <v-alert v-else type="info" outlined class="ma-4">
                    Please select a skill above to manage its steps.
                  </v-alert>
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Syllabus Activation Confirmation Dialog -->
    <v-dialog v-model="activateSyllabusDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Activate Syllabus
        </v-card-title>
        <v-card-text>
          <p>
            You are about to activate
            <strong>{{
              pendingSyllabusToActivate ? pendingSyllabusToActivate.name : ""
            }}</strong>
            ({{
              pendingSyllabusToActivate
                ? pendingSyllabusToActivate.version
                : ""
            }}).
          </p>
          <p v-if="currentActiveSyllabus">
            The currently active syllabus
            <strong>{{ currentActiveSyllabus.name }}</strong> will be
            deactivated.
          </p>
          <p>
            Only one syllabus can be active at a time. Do you wish to proceed?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="cancelSyllabusActivation"
            :disabled="activationLoading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmSyllabusActivation"
            :loading="activationLoading"
          >
            Confirm Activation
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { required, maxLength, integer } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";
import {
  M_OU,
  LOAD_SYLLABUS_LIST,
  LOAD_SKILL_LIST,
  LOAD_STEP_LIST,
  GET_SYLLABUS_LIST,
  GET_SKILL_LIST,
  GET_STEP_LIST,
  IS_SYLLABUS_LIST_LOADED,
  IS_SKILL_LIST_LOADED,
  IS_STEP_LIST_LOADED,
  ACTIVATE_SYLLABUS,
  DEACTIVATE_SYLLABUS,
  SAVE_NEW_SYLLABUS,
  ACTIVATE_SKILL,
  DEACTIVATE_SKILL,
  SAVE_NEW_SKILL,
  ACTIVATE_STEP,
  DEACTIVATE_STEP,
  SAVE_NEW_STEP,
} from "@/store/mutation-list";

// Custom validator: letters, numbers, dots and hyphens (empty passes — required handles that)
const versionFormat = (value) => !value || /^[a-zA-Z0-9.-]+$/.test(value);

export default {
  name: "ManageSyllabus",

  validations() {
    return {
      syllabusForm: {
        name: { required, maxLength: maxLength(150) },
        version: { required, maxLength: maxLength(20), versionFormat },
        effectiveFrom: { required },
      },
      skillForm: {
        skillNumber: {
          required,
          integer,
          consecutive: (value) => parseInt(value) === this.nextSkillNumber,
        },
        skillName: { required, maxLength: maxLength(150) },
        skillDescription: { maxLength: maxLength(500) },
      },
      stepForm: {
        stepNumber: {
          required,
          integer,
          consecutive: (value) => parseInt(value) === this.nextStepNumber,
        },
        stepName: { required, maxLength: maxLength(150) },
        stepDescription: { maxLength: maxLength(500) },
      },
    };
  },

  data() {
    return {
      activeTab: "tab-syllabuses",

      breadcrumbs: [
        { text: "Dashboard", disabled: false, href: "/dashboard/home" },
        {
          text: "Syllabus & Learning Materials",
          disabled: true,
          href: "/dashboard/managesyllabus",
        },
      ],

      // Syllabuses
      syllabuses: [],
      syllabusForm: {
        name: "",
        version: "",
        effectiveFrom: "",
      },
      syllabusSubmitting: false,
      effectiveFromMenu: false,

      // Syllabus activation
      activateSyllabusDialog: false,
      pendingSyllabusToActivate: null,
      activationLoading: false,

      syllabusHeaders: [
        { text: "ID", value: "id", width: "60px" },
        { text: "Name", value: "name" },
        { text: "Version", value: "version", width: "100px" },
        { text: "Effective From", value: "effective_from", width: "150px" },
        { text: "Active", value: "status", sortable: false, width: "90px" },
        { text: "Created On", value: "created_on", width: "160px" },
      ],

      // Skills
      selectedSyllabusForSkills: null,

      skillTableLoading: false,
      skillSubmitting: false,
      skillForm: {
        skillNumber: null,
        skillName: "",
        skillDescription: "",
      },
      skillHeaders: [
        { text: "#", value: "skill_number", width: "60px" },
        { text: "Skill Name", value: "skill_name" },
        { text: "Description", value: "skill_description" },
        { text: "Active", value: "status", sortable: false, width: "90px" },
        { text: "Created On", value: "created_on", width: "160px" },
      ],

      // Steps
      selectedSyllabusForSteps: null,
      selectedSkillForSteps: null,

      stepTableLoading: false,
      stepSubmitting: false,
      stepForm: {
        stepNumber: null,
        stepName: "",
        stepDescription: "",
      },
      stepHeaders: [
        { text: "#", value: "step_number", width: "60px" },
        { text: "Step Name", value: "step_name" },
        { text: "Description", value: "step_description" },
        { text: "Active", value: "status", sortable: false, width: "90px" },
        { text: "Created On", value: "created_on", width: "160px" },
      ],
    };
  },

  computed: {
    ...mapGetters({
      getSyllabuses: `${M_OU}${GET_SYLLABUS_LIST}`,
      getSkills: `${M_OU}${GET_SKILL_LIST}`,
      getSteps: `${M_OU}${GET_STEP_LIST}`,
    }),
    tomorrowDate() {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d.toISOString().substr(0, 10);
    },

    currentActiveSyllabus() {
      return this.getSyllabuses.find((s) => s.status === 1) || null;
    },

    skillsForSelectedSyllabus() {
      if (!this.selectedSyllabusForSkills) return [];
      return this.getSkills.filter(
        (sk) => sk.syllabus_id === this.selectedSyllabusForSkills
      );
    },

    skillsForStepsDropdown() {
      if (!this.selectedSyllabusForSteps) return [];
      return this.getSkills.filter(
        (sk) => sk.syllabus_id === this.selectedSyllabusForSteps
      );
    },

    stepsForSelectedSkill() {
      if (!this.selectedSkillForSteps) return [];
      return this.getSteps.filter(
        (st) => st.skill_id === this.selectedSkillForSteps
      );
    },

    nextSkillNumber() {
      const existing = this.skillsForSelectedSyllabus;
      if (!existing.length) return 1;
      return Math.max(...existing.map((sk) => sk.skill_number)) + 1;
    },

    nextStepNumber() {
      const existing = this.stepsForSelectedSkill;
      if (!existing.length) return 1;
      return Math.max(...existing.map((st) => st.step_number)) + 1;
    },

    // Validation error messages
    syllabusNameErrors() {
      const errors = [];
      if (!this.$v.syllabusForm.name.$dirty) return errors;
      if (!this.$v.syllabusForm.name.required)
        errors.push("Syllabus name is required.");
      if (!this.$v.syllabusForm.name.maxLength)
        errors.push("Name cannot exceed 150 characters.");
      return errors;
    },

    syllabusVersionErrors() {
      const errors = [];
      if (!this.$v.syllabusForm.version.$dirty) return errors;
      if (!this.$v.syllabusForm.version.required)
        errors.push("Version is required.");
      if (!this.$v.syllabusForm.version.maxLength)
        errors.push("Version cannot exceed 20 characters.");
      if (!this.$v.syllabusForm.version.versionFormat)
        errors.push(
          "Version may only contain letters, numbers, dots and hyphens."
        );
      return errors;
    },

    syllabusEffectiveFromErrors() {
      const errors = [];
      if (!this.$v.syllabusForm.effectiveFrom.$dirty) return errors;
      if (!this.$v.syllabusForm.effectiveFrom.required)
        errors.push("Effective From date is required.");
      return errors;
    },

    skillNumberErrors() {
      const errors = [];
      if (!this.$v.skillForm.skillNumber.$dirty) return errors;
      if (!this.$v.skillForm.skillNumber.required)
        errors.push("Skill number is required.");
      if (!this.$v.skillForm.skillNumber.integer)
        errors.push("Skill number must be a whole number.");
      if (!this.$v.skillForm.skillNumber.consecutive)
        errors.push(
          `Skill number must be ${this.nextSkillNumber} (next consecutive number).`
        );
      return errors;
    },

    skillNameErrors() {
      const errors = [];
      if (!this.$v.skillForm.skillName.$dirty) return errors;
      if (!this.$v.skillForm.skillName.required)
        errors.push("Skill name is required.");
      if (!this.$v.skillForm.skillName.maxLength)
        errors.push("Skill name cannot exceed 150 characters.");
      return errors;
    },

    skillDescriptionErrors() {
      const errors = [];
      if (!this.$v.skillForm.skillDescription.$dirty) return errors;
      if (!this.$v.skillForm.skillDescription.maxLength)
        errors.push("Description cannot exceed 500 characters.");
      return errors;
    },

    stepNumberErrors() {
      const errors = [];
      if (!this.$v.stepForm.stepNumber.$dirty) return errors;
      if (!this.$v.stepForm.stepNumber.required)
        errors.push("Step number is required.");
      if (!this.$v.stepForm.stepNumber.integer)
        errors.push("Step number must be a whole number.");
      if (!this.$v.stepForm.stepNumber.consecutive)
        errors.push(
          `Step number must be ${this.nextStepNumber} (next consecutive number).`
        );
      return errors;
    },

    stepNameErrors() {
      const errors = [];
      if (!this.$v.stepForm.stepName.$dirty) return errors;
      if (!this.$v.stepForm.stepName.required)
        errors.push("Step name is required.");
      if (!this.$v.stepForm.stepName.maxLength)
        errors.push("Step name cannot exceed 150 characters.");
      return errors;
    },

    stepDescriptionErrors() {
      const errors = [];
      if (!this.$v.stepForm.stepDescription.$dirty) return errors;
      if (!this.$v.stepForm.stepDescription.maxLength)
        errors.push("Description cannot exceed 500 characters.");
      return errors;
    },
  },

  methods: {
    ...mapActions({
      loadSyllabuses: `${M_OU}${LOAD_SYLLABUS_LIST}`,
      isSyllabusListLoaded: `${M_OU}${IS_SYLLABUS_LIST_LOADED}`,
      addSyllabus: `${M_OU}${SAVE_NEW_SYLLABUS}`,
      activateSyllabus: `${M_OU}${ACTIVATE_SYLLABUS}`,
      deactivateSyllabus: `${M_OU}${DEACTIVATE_SYLLABUS}`,

      loadSkills: `${M_OU}${LOAD_SKILL_LIST}`,
      isSkillListLoaded: `${M_OU}${IS_SKILL_LIST_LOADED}`,
      addSkill: `${M_OU}${SAVE_NEW_SKILL}`,
      activateSkill: `${M_OU}${ACTIVATE_SKILL}`,
      deactivateSkill: `${M_OU}${DEACTIVATE_SKILL}`,

      loadSteps: `${M_OU}${LOAD_STEP_LIST}`,
      isStepListLoaded: `${M_OU}${IS_STEP_LIST_LOADED}`,
      saveStep: `${M_OU}${SAVE_NEW_STEP}`,
      activateStep: `${M_OU}${ACTIVATE_STEP}`,
      deactivateStep: `${M_OU}${DEACTIVATE_STEP}`,
    }),
    showAlert(type, title, message) {
      this.$fire({ title, text: message, type, timer: 3000 });
    },

    // ---- Syllabus ----
    resetSyllabusForm() {
      this.syllabusForm = { name: "", version: "", effectiveFrom: "" };
      this.$v.syllabusForm.$reset();
    },

    submitSyllabus() {
      this.$v.syllabusForm.$touch();
      if (this.$v.syllabusForm.$invalid) {
        this.showAlert(
          "error",
          "Validation Error",
          "Please fix the errors before submitting."
        );
        return;
      }
      this.syllabusSubmitting = true;
      //apiAddSyllabus({
      this.addSyllabus({
        name: this.syllabusForm.name,
        version: this.syllabusForm.version,
        effective_from: this.syllabusForm.effectiveFrom,
      })
        .then((res) => {
          if (res === "SUCCESS") {
            this.showAlert(
              "success",
              "Success",
              "Syllabus added successfully."
            );
            this.resetSyllabusForm();
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to add syllabus. Please try again."
            );
          }
        })
        .finally(() => {
          this.syllabusSubmitting = false;
        });
    },

    onSyllabusRowClick(item) {
      this.selectedSyllabusForSkills = item.id;
      this.activeTab = "tab-skills";
    },

    onSyllabusToggle(item) {
      if (item.status === 1) return; // already active, switch is disabled
      this.pendingSyllabusToActivate = item;
      this.activateSyllabusDialog = true;
    },

    cancelSyllabusActivation() {
      this.activateSyllabusDialog = false;
      this.pendingSyllabusToActivate = null;
    },

    confirmSyllabusActivation() {
      this.activationLoading = true;
      const targetId = this.pendingSyllabusToActivate.id;
      const currentActive = this.currentActiveSyllabus;

      const activateCall = this.activateSyllabus(targetId);
      const deactivateCall = currentActive
        ? this.deactivateSyllabus(currentActive.id)
        : Promise.resolve("SUCCESS");

      Promise.all([activateCall, deactivateCall])
        .then(([activateRes, deactivateRes]) => {
          if (activateRes === "SUCCESS" && deactivateRes === "SUCCESS") {
            // Update local state
            this.syllabuses = this.syllabuses.map((s) => {
              if (s.id === targetId) return { ...s, status: 1 };
              if (currentActive && s.id === currentActive.id)
                return { ...s, status: 0 };
              return s;
            });
            this.showAlert(
              "success",
              "Activated",
              `"${this.pendingSyllabusToActivate.name}" is now the active syllabus.`
            );
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to update syllabus status. Please try again."
            );
          }
        })
        .finally(() => {
          this.activationLoading = false;
          this.activateSyllabusDialog = false;
          this.pendingSyllabusToActivate = null;
        });
    },

    // ---- Skills ----
    onSyllabusSelectedForSkills() {
      this.$v.skillForm.$reset();
      this.resetSkillForm();
    },

    resetSkillForm() {
      this.skillForm = {
        skillNumber: null,
        skillName: "",
        skillDescription: "",
      };
      this.$v.skillForm.$reset();
    },

    submitSkill() {
      this.$v.skillForm.$touch();
      if (this.$v.skillForm.$invalid) {
        this.showAlert(
          "error",
          "Validation Error",
          "Please fix the errors before submitting."
        );
        return;
      }
      this.skillSubmitting = true;
      this.addSkill({
        syllabus_id: this.selectedSyllabusForSkills,
        skill_number: this.skillForm.skillNumber,
        skill_name: this.skillForm.skillName,
        skill_description: this.skillForm.skillDescription,
      })
        .then((res) => {
          if (res === "SUCCESS") {
            this.showAlert("success", "Success", "Skill added successfully.");
            this.resetSkillForm();
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to add skill. Please try again."
            );
          }
        })
        .finally(() => {
          this.skillSubmitting = false;
        });
    },

    onSkillRowClick(item) {
      this.selectedSyllabusForSteps = item.syllabus_id;
      this.selectedSkillForSteps = item.skill_id;
      this.activeTab = "tab-steps";
    },

    onSkillToggle(item, newVal) {
      const newStatus = newVal ? 1 : 0;
      if (newStatus === 1) {
        //activate
        this.activateSkill(item.skill_id).then((res) => {
          if (res === "SUCCESS") {
            this.showAlert(
              "success",
              "Activated",
              `"${item.skill_name}" is now active skill.`
            );
          } else {
            this.showAlert("error", "Error", "Failed to activate skill.");
          }
        });
      } else {
        //deactivate
        this.deactivateSkill(item.skill_id).then((res) => {
          if (res === "SUCCESS") {
            this.showAlert(
              "success",
              "Deactivated",
              `"${item.skill_name}" is now inactive skill.`
            );
          } else {
            this.showAlert("error", "Error", "Failed to deactivate skill.");
          }
        });
      }
    },

    // ---- Steps ----
    onSyllabusSelectedForSteps() {
      this.selectedSkillForSteps = null;
      this.$v.stepForm.$reset();
      this.resetStepForm();
    },

    onSkillSelectedForSteps() {
      this.$v.stepForm.$reset();
      this.resetStepForm();
    },

    resetStepForm() {
      this.stepForm = { stepNumber: null, stepName: "", stepDescription: "" };
      this.$v.stepForm.$reset();
    },

    submitStep() {
      this.$v.stepForm.$touch();
      if (this.$v.stepForm.$invalid) {
        this.showAlert(
          "error",
          "Validation Error",
          "Please fix the errors before submitting."
        );
        return;
      }
      this.stepSubmitting = true;
      this.saveStep({
        skill_id: this.selectedSkillForSteps,
        step_number: this.stepForm.stepNumber,
        step_name: this.stepForm.stepName,
        step_description: this.stepForm.stepDescription,
      })
        .then((res) => {
          if (res === "SUCCESS") {
            this.showAlert("success", "Success", "Step added successfully.");
            this.resetStepForm();
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to add step. Please try again."
            );
          }
        })
        .finally(() => {
          this.stepSubmitting = false;
        });
    },

    onStepToggle(item, newVal) {
      const newStatus = newVal ? 1 : 0;
      //   apiToggleStep(item.step_id, newStatus).then((res) => {
      if (newStatus === 1) {
        this.activateStep(item.step_id, newStatus).then((res) => {
          if (res === "SUCCESS") {
            this.showAlert(
              "success",
              "Activated",
              `"${item.step_name}" is now the active step.`
            );
          } else {
            this.showAlert("error", "Error", "Failed to update step status.");
          }
        });
      } else {
        this.deactivateStep(item.step_id, newStatus).then((res) => {
          if (res === "SUCCESS") {
            this.showAlert(
              "success",
              "Deactivated",
              `"${item.step_name}" is now an inactive step.`
            );
          } else {
            this.showAlert("error", "Error", "Failed to update step status.");
          }
        });
      }
    },
  },

  created() {
    this.loadSyllabuses();
    this.loadSkills();
    this.loadSteps();
  },
};
</script>

<style scoped>
.section-subtitle {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 4px 0 0;
}
</style>
